import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Contact } from '@/hooks/useContacts';
import { useContactCommunications } from '@/hooks/useContactCommunications';
import { useToast } from '@/hooks/use-toast';
import { useEmailDetails } from '@/hooks/useEmailDetails';
import { Mail, MessageSquare, Send, Phone, Calendar, User, StickyNote, Paperclip, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { AttachmentList } from '@/components/email/AttachmentList';

interface CommunicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
}

const communicationIcons = {
  email: Mail,
  sms: MessageSquare,
  call: Phone,
  meeting: Calendar,
  note: StickyNote,
};

const statusColors = {
  sent: 'bg-blue-100 text-blue-800',
  delivered: 'bg-green-100 text-green-800',
  read: 'bg-purple-100 text-purple-800',
  failed: 'bg-red-100 text-red-800',
  completed: 'bg-green-100 text-green-800',
  scheduled: 'bg-yellow-100 text-yellow-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

function EmailAttachmentsSection({ emailMessageId }: { emailMessageId: string }) {
  const { data: emailDetails, isLoading } = useEmailDetails(emailMessageId);
  
  if (isLoading) {
    return (
      <div className="mt-4 pt-4 border-t">
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  
  if (!emailDetails) return null;
  
  return (
    <div className="mt-4 pt-4 border-t">
      <AttachmentList
        emailId={emailMessageId}
        emailAccountId={emailDetails.email_account_id}
        messageId={emailDetails.message_id}
      />
    </div>
  );
}

export function CommunicationDialog({ open, onOpenChange, contact }: CommunicationDialogProps) {
  const { toast } = useToast();
  const { communications, isLoading, createCommunication } = useContactCommunications(contact?.id || null);
  
  const [activeTab, setActiveTab] = useState('history');
  const [smsData, setSmsData] = useState({ message: '' });
  const [callData, setCallData] = useState({ notes: '', duration: '' });
  const [noteData, setNoteData] = useState({ subject: '', notes: '' });
  const [sending, setSending] = useState(false);

  const handleSendSMS = async () => {
    if (!contact || !smsData.message.trim()) {
      toast({
        title: "Error",
        description: "Please enter a message",
        variant: "destructive"
      });
      return;
    }

    setSending(true);
    try {
      await createCommunication({
        contact_id: contact.id,
        communication_type: 'sms',
        direction: 'outbound',
        message: smsData.message,
        status: 'sent',
        completed_at: new Date(),
      });
      
      setSmsData({ message: '' });
      setActiveTab('history');
    } catch (error) {
      // Error handled by hook
    } finally {
      setSending(false);
    }
  };

  const handleLogCall = async () => {
    if (!contact || !callData.notes.trim()) {
      toast({
        title: "Error",
        description: "Please enter call notes",
        variant: "destructive"
      });
      return;
    }

    setSending(true);
    try {
      await createCommunication({
        contact_id: contact.id,
        communication_type: 'call',
        direction: 'outbound',
        message: callData.notes,
        status: 'completed',
        completed_at: new Date(),
        duration_minutes: callData.duration ? parseInt(callData.duration) : undefined,
      });
      
      setCallData({ notes: '', duration: '' });
      setActiveTab('history');
    } catch (error) {
      // Error handled by hook
    } finally {
      setSending(false);
    }
  };

  const handleAddNote = async () => {
    if (!contact || !noteData.notes.trim()) {
      toast({
        title: "Error",
        description: "Please enter note content",
        variant: "destructive"
      });
      return;
    }

    setSending(true);
    try {
      await createCommunication({
        contact_id: contact.id,
        communication_type: 'note',
        direction: 'bidirectional',
        subject: noteData.subject,
        message: noteData.notes,
        status: 'completed',
        completed_at: new Date(),
      });
      
      setNoteData({ subject: '', notes: '' });
      setActiveTab('history');
    } catch (error) {
      // Error handled by hook
    } finally {
      setSending(false);
    }
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Communication with {contact.name}
          </DialogTitle>
          <DialogDescription>
            View history and log communications
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 h-auto p-1 bg-muted/50">
              <TabsTrigger value="history" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                <Clock className="h-4 w-4" />
                History
              </TabsTrigger>
              <TabsTrigger value="sms" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                <MessageSquare className="h-4 w-4" />
                SMS
              </TabsTrigger>
              <TabsTrigger value="call" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                <Phone className="h-4 w-4" />
                Call
              </TabsTrigger>
              <TabsTrigger value="note" className="flex items-center justify-center gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-primary/90 data-[state=active]:to-primary data-[state=active]:text-primary-foreground">
                <StickyNote className="h-4 w-4" />
                Note
              </TabsTrigger>
            </TabsList>
            
              <TabsContent value="history" className="space-y-4">
                {isLoading ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Loading communications...</p>
                  </div>
                ) : communications.length === 0 ? (
                  <div className="text-center py-12 px-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                      <MessageSquare className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Track all interactions with {contact.name}</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Keep a complete history of emails, calls, meetings, and notes in one place
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <Button 
                        variant="outline" 
                        onClick={() => setActiveTab('call')}
                        className="gap-2"
                      >
                        <Phone className="h-4 w-4" />
                        Log Call
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setActiveTab('note')}
                        className="gap-2"
                      >
                        <StickyNote className="h-4 w-4" />
                        Add Quick Note
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {communications.map((record, index) => {
                      const Icon = communicationIcons[record.communication_type];
                      const displayDate = record.completed_at || record.scheduled_at || record.created_at;
                      const emailId = record.metadata?.email_message_id;
                      const hasAttachments = record.metadata?.has_attachments;
                      
                      return (
                        <div key={record.id}>
                          <Card>
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4" />
                                  <span className="font-medium capitalize">{record.communication_type}</span>
                                  <Badge 
                                    variant={record.direction === 'outbound' ? 'default' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {record.direction}
                                  </Badge>
                                  <Badge 
                                    variant="outline" 
                                    className={`text-xs ${statusColors[record.status as keyof typeof statusColors] || statusColors.completed}`}
                                  >
                                    {record.status}
                                  </Badge>
                                  {record.duration_minutes && (
                                    <Badge variant="outline" className="text-xs">
                                      {record.duration_minutes} min
                                    </Badge>
                                  )}
                                  {hasAttachments && (
                                    <Badge variant="outline" className="text-xs flex items-center gap-1">
                                      <Paperclip className="h-3 w-3" />
                                      Attachments
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-sm text-muted-foreground">
                                  {format(new Date(displayDate), 'PPp')}
                                </span>
                              </div>
                              {record.subject && (
                                <CardTitle className="text-base">{record.subject}</CardTitle>
                              )}
                            </CardHeader>
                            <CardContent className="pt-0">
                              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{record.message}</p>
                              
                              {/* Show attachments inline for emails */}
                              {record.communication_type === 'email' && hasAttachments && emailId && (
                                <EmailAttachmentsSection emailMessageId={emailId} />
                              )}
                            </CardContent>
                          </Card>
                          {index < communications.length - 1 && (
                            <Separator className="my-4" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="sms" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Log SMS to {contact.name}
                    </CardTitle>
                    <CardDescription>
                      {contact.phone ? `Phone: ${contact.phone}` : 'No phone number on file'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="sms-message">Message</Label>
                      <Textarea
                        id="sms-message"
                        value={smsData.message}
                        onChange={(e) => setSmsData({ message: e.target.value })}
                        placeholder="Type your SMS message here..."
                        rows={4}
                        maxLength={160}
                      />
                      <div className="text-right text-xs text-muted-foreground">
                        {smsData.message.length}/160 characters
                      </div>
                    </div>
                    <Button 
                      onClick={handleSendSMS} 
                      disabled={sending || !smsData.message.trim()}
                      className="w-full"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {sending ? 'Logging...' : 'Log SMS'}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="call" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="h-5 w-5" />
                      Log Call with {contact.name}
                    </CardTitle>
                    <CardDescription>
                      Record details about a phone call
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="call-duration">Duration (minutes, optional)</Label>
                      <Input
                        id="call-duration"
                        type="number"
                        value={callData.duration}
                        onChange={(e) => setCallData(prev => ({ ...prev, duration: e.target.value }))}
                        placeholder="e.g., 15"
                        min="1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="call-notes">Call Notes</Label>
                      <Textarea
                        id="call-notes"
                        value={callData.notes}
                        onChange={(e) => setCallData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="What was discussed during the call..."
                        rows={6}
                      />
                    </div>
                    <Button 
                      onClick={handleLogCall} 
                      disabled={sending || !callData.notes.trim()}
                      className="w-full"
                    >
                      <Phone className="h-4 w-4 mr-2" />
                      {sending ? 'Logging...' : 'Log Call'}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="note" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <StickyNote className="h-5 w-5" />
                      Add Note about {contact.name}
                    </CardTitle>
                    <CardDescription>
                      Record any additional information or observations
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="note-subject">Subject (optional)</Label>
                      <Input
                        id="note-subject"
                        value={noteData.subject}
                        onChange={(e) => setNoteData(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="Note subject..."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="note-notes">Notes</Label>
                      <Textarea
                        id="note-notes"
                        value={noteData.notes}
                        onChange={(e) => setNoteData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Type your notes here..."
                        rows={6}
                      />
                    </div>
                    <Button 
                      onClick={handleAddNote} 
                      disabled={sending || !noteData.notes.trim()}
                      className="w-full"
                    >
                      <StickyNote className="h-4 w-4 mr-2" />
                      {sending ? 'Saving...' : 'Save Note'}
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
